import { useTranslation } from "react-i18next"

import type { CompetitorSectionKey } from "./competitor-plan-constants"
import type { CompetitorPlan, FieldCitation, FieldUpdatePayload, PlanSource } from "../../types"

import { ManualRelationshipsSection } from "./manual-relationships-section"
import { FieldValueView } from "../plan/plan-field"

type OnCitationClick = (citation: FieldCitation) => void

/** Renders the inner content for a single Competitor Plan section. */
export function CompetitorSectionBody({
  sectionKey,
  plan,
  canEdit,
  isSavingManual,
  onSaveManual,
  onCitationClick,
  sources,
}: {
  sectionKey: CompetitorSectionKey
  plan: CompetitorPlan
  canEdit: boolean
  isSavingManual: boolean
  onSaveManual: (payload: FieldUpdatePayload) => void
  onCitationClick?: OnCitationClick
  sources?: PlanSource[]
}) {
  const { t } = useTranslation()
  const s = plan.sections
  switch (sectionKey) {
    case "overview":
    case "businessModel":
    case "marketOverview":
    case "keyActivities":
      return (
        <FieldValueView field={s[sectionKey]} sources={sources} onCitationClick={onCitationClick} />
      )
    case "strengthsWeaknesses":
      return (
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <h4 className="text-success mb-2 text-sm font-semibold">
              {t("segmentAnalysis.plan.strengths")}
            </h4>
            <div className="space-y-2">
              {s.strengthsWeaknesses.strengths.map((field, i) => (
                <FieldValueView
                  key={i}
                  field={field}
                  sources={sources}
                  onCitationClick={onCitationClick}
                />
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-destructive mb-2 text-sm font-semibold">
              {t("segmentAnalysis.plan.weaknesses")}
            </h4>
            <div className="space-y-2">
              {s.strengthsWeaknesses.weaknesses.map((field, i) => (
                <FieldValueView
                  key={i}
                  field={field}
                  sources={sources}
                  onCitationClick={onCitationClick}
                />
              ))}
            </div>
          </div>
        </div>
      )
    case "nolatoRelationships":
      return (
        <ManualRelationshipsSection
          canEdit={canEdit}
          field={s.nolatoRelationships}
          isSaving={isSavingManual}
          onSave={onSaveManual}
        />
      )
    default: {
      const _exhaustive: never = sectionKey
      return _exhaustive
    }
  }
}
